"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface UserAvatarProps {
    name: string;
    src?: string | null;
    className?: string;
    size?: "sm" | "md" | "lg";
}

const avatarSizeClasses: Record<NonNullable<UserAvatarProps["size"]>, string> = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12",
};

const textSizeClasses: Record<NonNullable<UserAvatarProps["size"]>, string> = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
};


const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";

    const initials = parts
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("");

    return initials;
};

export function UserAvatar({ name, src, className, size = "md" }: UserAvatarProps) {
    const initials = getInitials(name);
    const avatarSizeClass = avatarSizeClasses[size];
    const textSizeClass = textSizeClasses[size];

    return (
        <Avatar className={cn(avatarSizeClass, className)}>
            <AvatarImage src={src ?? undefined} />
            <AvatarFallback
                className={cn(
                    "bg-gray-200 text-gray-900 font-medium",
                    avatarSizeClass,
                    textSizeClass,
                )}
            >
                {initials}
            </AvatarFallback>
        </Avatar>
    );
}
